import _, { eq } from 'lodash';
import moment from 'moment';
import { getDisplayDate, utc } from '../../../libs/date';
import { evaluate as equationEvaluate } from 'mathjs';
import syncLoop from '../../../libs/syncLoop';
import { dischargeGenerate } from './dischargeGenerator';

const formats = [null, 'YYYY-MM-DDTHH:00:00', 'YYYY-MM-DDT00:00:00', 'YYYY-MM-01T00:00:00', 'YYYY-01-01T00:00:00', 'YYYY-MM-DDTHH:00:00'];
const method = [
  null,
  values => _.min(Object.values(values)),
  values => _.max(Object.values(values)),
  values => _.mean(Object.values(values)),
  values => _.sum(Object.values(values)),
  (values, period) => values[period],
];
const types = [null, 'hours', 'days', 'months', 'years', 'hours'];
const periodIntervalMultiplier = [null, 1, 24, 24 * 30, 24 * 30 * 12, 3];
const periodLengths = [null, 24, 31, 12];
const periodNames = [null, 'hour', 'day', 'month'];
const periodFormats = [null, 'HH', 'DD', 'MM'];
const convertCustomHourlyPeriod = hours => {
  if (hours >= periodIntervalMultiplier[1] && hours < periodIntervalMultiplier[2]) return 1;
  if (hours >= periodIntervalMultiplier[2] && hours < periodIntervalMultiplier[3]) return 2;
  if (hours >= periodIntervalMultiplier[3] && hours < periodIntervalMultiplier[4]) return 3;
  if (hours >= periodIntervalMultiplier[4]) return 4;
  return 1;
}

const resolveMetaData = (col, { station }) => {
  if (+col.key < 0) {
    switch (+col.key) {
      case -1: return station.name;
      case -2: return station.descrpition;
      case -3: return station.latitude;
      case -4: return station.longitude;
      case -5: return station.elevation;
      case -6: return station.id;
      case -7: return station.identifier;
      default: return '-';
    }
  } else {
    let meta_data = !!station.meta_data && station.meta_data.find(item => + item.id === +col.key);
    return !!meta_data ? meta_data.value : '-';
  }
}

const roundOff = (value, precision = 2) => !!parseFloat(value) ? Math.round(+value * Math.pow(10, precision)) / Math.pow(10, precision) : value;
const resolveParameter = (col, { data }) => {
  let value = data[col.label];
  return !!value || value == 0 ? roundOff(value, col.precision || 2) : '-';
}

const resolveDate = (col, { date }) => getDisplayDate(date, { format: col.key });

const resolveLocation = ({ station }, { locationStore }) => {
  let location = locationStore.findById(station.location_id);
  return !!location ? locationStore.getFullLocation(location) : '-';
}

const resolveStationType = ({ station }, { stationTypeStore }) => {
  let stationType = stationTypeStore.findById(station.station_type_id);
  return !!stationType ? stationType.name : '-';
}

const resolveEquation = ({ equation, equation_parameters, precision }, { data }) => {
  let parameters = equation_parameters.split(',')
  let equationWithParameters = parameters.reduce((acc, parameter, i) => acc.replace(`p${i}`, data[parameter]), equation);
  if (equationWithParameters.indexOf('undefined') > -1) return '-';
  return roundOff(equationEvaluate(equationWithParameters), precision);
}

const resolveField = (col, data, prevData, store) => {
  let value = (() => {
    switch (+col.type) {
      case 1: return data.sn;
      case 2: return resolveMetaData(col, data);
      case 3: return resolveParameter(col, data);
      case 4: return resolveLocation(data, store);
      case 5: return resolveStationType(data, store);
      case 6: return resolveDate(col, data);
      case 7: return resolveEquation(col, data);
      case 8: return !prevData ? '-' : resolveDate(col, prevData);
      default: return '-';
    }
  })()
  return !!value || value == 0 ? value.toString().replace(/,/g, '_') : '-';
}

const aggregate = ({ data, parameter_interval }, { agg_method, agg_period, agg_period_hour, agg_offset, max_missing_values, missing_data_symbol, agg_type, group_by_period, precision }, station) => new Promise(async (resolve, reject) => {
  try {
    if ([6, 7].includes(+agg_method)) {
      let discharge = await dischargeGenerate(data, station);
      if (+agg_method === 7) discharge = await aggregate({ data: discharge }, { agg_method: 3, agg_period: 2, precision });
      resolve(discharge);
      return;
    }

    let periodIntervalValueCount = (() => {
      if (!parameter_interval) return null;
      let hourlyCount = Math.floor(60 / +parameter_interval);
      return hourlyCount * periodIntervalMultiplier[+agg_period];
    })();

    let agg_period_day = Math.floor(agg_period_hour / 24);
    let periodicData = _.groupBy(data, item => {
      if(agg_period === 6) {
        if(agg_type === 2) return data[data.length - 1].time;
        else return data[0].time;
      }
      let displayTime = getDisplayDate(item.time);
      let hour = +moment.utc(displayTime).format('HH');
      let day = +moment.utc(displayTime).format('DD');
      let periodHour = agg_period === 5 ? Math.floor(hour / agg_period_hour) * agg_period_hour : hour;
      let aggDayOffset = (day % agg_period_day) + (day > 30 ? 10 : 0) + (day % agg_period_day === 0 ? agg_period_day - 1 : -1);

      const period = utc(moment(
        moment(displayTime)
          .subtract(aggDayOffset, 'days')
          .hours(periodHour)
          .subtract(agg_offset || 0, 'minutes')
          .subtract(agg_type === 2 && agg_period !== 5 ? 1 : 0, 'seconds')
          .add(+agg_type === 2 ? (agg_period === 5 ? agg_period_hour : 1) : 0, types[agg_period])
          // .add(+agg_type === 2 ? 1 : 0, 'seconds')
          .format(formats[agg_period === 5 ? convertCustomHourlyPeriod(agg_period_hour) : agg_period])
      ).add(agg_offset || 0, 'minutes').format('YYYY-MM-DDTHH:mm:ss'));
      // console.log(displayTime, day, aggDayOffset, period, agg_period_hour)
      return period;
    });
    // console.log(periodicData);
    // Object.keys(periodicData).forEach(period => {
    //   console.log(getDisplayDate(period), periodicData[period].map(item => ([item.time, getDisplayDate(item.time), item.value])))
    // })

    let aggregatedData = Object.keys(periodicData).map((period) => {
      let values = periodicData[period].reduce((acc, item) => {
        if (!!+item.value || +item.value === 0) acc[moment.utc(item.time).format('YYYY-MM-DDTHH:mm:ss')] = roundOff(+item.value, precision);
        return acc;
      }, {});
      const getMissingValuesPercent = (100 * (periodIntervalValueCount - Object.keys(values).length)) / periodIntervalValueCount;
      // console.log(getDisplayDate(period), Object.keys(values).map(item => ([getDisplayDate(item), values[item]])));
      return ({
        time: period,
        value: !!periodIntervalValueCount && getMissingValuesPercent >= max_missing_values ? missing_data_symbol : method[agg_method](values, period)
      });
    });

    if (!group_by_period || +agg_period > 3) return resolve(aggregatedData);
    else {
      let periodGroup = _.groupBy(aggregatedData, item => moment.utc(item.time).format(formats[+agg_period + 1]));
      const parameterData = Object.keys(periodGroup).map(period => {
        let periodValues = [];
        periodGroup[period].forEach(item => {
          let periodGroupIndex = +moment.utc(item.time).format(periodFormats[agg_period]) + (agg_period === 3 ? -1 : 1);
          periodValues[periodGroupIndex] = item.value
        })
        return ({
          time: period,
          value: periodValues
        })
      });
      resolve(parameterData);
    }
  } catch (err) {
    reject(err);
  }
});

const getPeriodGroups = ({ agg_period, group_period_format }) => {
  const periodLength = periodLengths[agg_period];
  const periodName = periodNames[agg_period]
  return Array.from(Array(periodLength).keys()).map(item => ({
    label: moment()[periodName](item).format(group_period_format),
    index: item
  }))
}

export const reportGenerator = async (stations, template, store) => {
  let csv = [template.columns.map(item => item.label).join(',')];

  let stationsTuple = {};
  let data = {};
  /*
    {
      [date]: {
        [station_id]: {
          [parameter]: value
        }
      }
    }
  */

  await syncLoop(stations, async station => {
    _.set(stationsTuple, `station_${station.id}`, _.omit(station, 'parameters'));
    if (!!station.parameters) {
      let parameterColumns = template.columns.filter(item => +item.type === 3);
      await syncLoop(parameterColumns, async col => {
        let parameter = station.parameters.find(item => +item.parameter_id === +col.key);
        if (!!parameter && !!parameter.data) {
          let isAggregated = (!!col.agg_method && !!col.agg_period) || ([6, 7].includes(+col.agg_method));
          let parameterData = await (isAggregated ? aggregate(parameter, col, station) : parameter.data);
          parameterData.forEach(item => {
            _.set(data, [moment.utc(item.time).format('YYYY-MM-DDTHH:mm:ss'), `station_${station.id}`, col.label], item.value)
          });
        }
      });
    }
    return Promise.resolve();
  });

  let sn = 0;
  const sortedDataKeys = Object.keys(data).sort((a, b) => moment(a).valueOf() < moment(b).valueOf() ? -1 : 1);
  sortedDataKeys.forEach((date, i) => {
    let row = []
    Object.keys(data[date]).forEach((station, j) => {
      sn++;
      template.columns.map(col => {
        if (col.group_by_period && +col.agg_period < 4) {
          let periodGroupHeader = [];
          let periodGroups = getPeriodGroups(col);
          periodGroups.forEach(pg => {
            periodGroupHeader.push(`${col.label} - ${pg.label}`)
            row.push(roundOff(data[date][station][col.label][pg.index], col.precision || 2) || '-')
          });
          // data[date][station][col.label].forEach(item => {
          //   periodGroupHeader.push(`${col.label} - ${moment.utc(item.time).format(col.group_period_format)}`)
          //   row.push(roundOff(item.value, col.precision || 2));
          // });
          let originalHeader = csv[0].split(',');
          let indexOfCol = originalHeader.indexOf(col.label);
          if (i === 0 && j == 0) csv[0] = [...originalHeader.slice(0, indexOfCol), ...periodGroupHeader, ...originalHeader.slice(indexOfCol + 1)].join(',');
        } else {
          row.push(
            resolveField(
              col,
              { sn, date, data: data[date][station], station: stationsTuple[station] },
              i > 0 ? { date: sortedDataKeys[i - 1], data: data[sortedDataKeys[i - 1]][station] } : null,
              store,
            )
          );
        }
      })
      csv.push(row.join(','));
      row = [];
    })
  });

  return Promise.resolve({
    report: csv.join('\n'),
    observations: data
  });

}


// WEBPACK FOOTER //
// ./src/components/DataDelivery/ReportingTemplate/reportGenerator.js